/**
 * Compact landscape HUD layout — W2a (2026-09-28).
 *
 * The short-viewport (≤ HUD_COMPACT_LAYOUT.maxViewportHeight = 450 px) band
 * stack that keeps the five phone-landscape surfaces disjoint:
 *
 *   map banner      top-centre            (unchanged)
 *   player info     top-left              (unchanged dock)
 *   target frame    left column, below player info, capped above the band
 *   quick menu      right column, below the banner clearance
 *   chat peek       right column, below the quick menu
 *   action bar      bottom band, scaled about its bottom-centre
 *
 * Everything derives from shared values — HUD_COMPACT_LAYOUT
 * (hudDefaults.config.ts), HUD_TOP_CONSTANTS (hudTopLayout.ts) and the natural
 * hotbar geometry exported by ActionBarStyles.tsx. No hand-placed pixels:
 *
 *   actionBarBandHeight  = round(ACTION_BAR_NATURAL_HEIGHT × scale)   (68)
 *   actionBarBandTop     = viewport − VIEWPORT_MARGIN − bandHeight
 *   chatBottomInset      = VIEWPORT_MARGIN + bandHeight + GAP          (94)
 *   chatTop              = chatBottomInset + chatHeight from the bottom
 *   chatWidth            = clamp(viewport − chat.rightInset −
 *                                (TARGET_PANEL_LEFT + PANEL_MAX_WIDTH) − GAP,
 *                                chat.minWidth, chat.maxWidth)
 *   quickMenuBottomY     = chatTop − GAP
 *   quickMenuTop         = max(banner clearance, measured minimap bottom + GAP)
 *                          when the remaining slot fits the documented minimum
 *                          usable panel height; otherwise the panel keeps the
 *                          banner clearance and its RIGHT edge moves to
 *                          measured minimap.left − GAP (horizontal clearance;
 *                          W2a-2 2026-09-28 — the below-minimap slot is 20 px
 *                          at 844×390 and 0 at 640×360, so vertical-only
 *                          placement would collapse the panel)
 *   quickMenuWidth       = clamp(minimap.left − GAP − LEFT_COLUMN_VISUAL_RIGHT
 *                                − GAP, minWidth, panel width)
 *   quickMenuMaxHeight   = quickMenuBottomY − quickMenuTop
 *   targetPanelMaxHeight = bandTop − GAP − targetPanelTop
 *
 * The minimap rect is MEASURED once per layout change by useHudMinimapRect
 * (targeted ResizeObserver + mount watcher — the W1a no-per-frame-DOM-read
 * pattern) and falls back to the documented shipped geometry (top/right 10,
 * 180×140 + frame) when absent/unmeasurable.
 *
 * The 640×360 numbers (capture, banner bottom 66): band 68 → bandTop 276,
 * chat y162..266 (298×104 at x326..624), quick menu y76..152 (162×76, left of
 * the minimap at x276..438), target y163..266 (left column), player info
 * y16..153. 844×390: bandTop 306, chat y192..296 (380×104 at x448..828), quick
 * menu y76..182 (196×106 at x446..642), target y163..296.
 *
 * Compact is a HEIGHT-only switch: 1280×720 (and every height > 450) keeps the
 * accepted desktop layout — the compact module returns isCompact:false there
 * and consumers fall back to the base styles.
 */
import { useEffect, useLayoutEffect, useState } from 'react';
import { HUD_COMPACT_LAYOUT, HUD_PANEL_DEFAULTS } from '../../../config/ui/hudDefaults.config';
import { ACTION_BAR_NATURAL_HEIGHT, ACTION_BAR_NATURAL_WIDTH } from '../Systems/ActionBar/ActionBarStyles';
import { HUD_TOP_CONSTANTS, computeBannerClearanceTop } from './hudTopLayout';

export const HUD_COMPACT_CONSTANTS = {
  /** Inter-surface gap — the frozen U01 element gap (10 px ≥ the 8 px floor). */
  GAP: HUD_TOP_CONSTANTS.ELEMENT_GAP,
  VIEWPORT_MARGIN: HUD_TOP_CONSTANTS.VIEWPORT_MARGIN,
  /**
   * Right edge of the left HUD column in compact mode: the target frame keeps
   * its PANEL_MAX_WIDTH at the docked TARGET_PANEL_LEFT. The chat peek's left
   * edge is always at least GAP right of this line.
   */
  LEFT_COLUMN_RIGHT: HUD_TOP_CONSTANTS.TARGET_PANEL_LEFT + HUD_TOP_CONSTANTS.PANEL_MAX_WIDTH,
  /**
   * VISUAL right edge of the docked left column (W2a-2): the unit frame and the
   * target frame both render at PANEL_MIN_WIDTH (250) from TARGET_PANEL_LEFT
   * (16) — `.player-info-panel { width: 250px }` at ≤1024 px and the target
   * panel's `minWidth`. Used as the left bound of the quick menu's
   * left-of-minimap anchor so the expanded panel clears the measured minimap
   * even at 640 px width without touching either frame.
   */
  LEFT_COLUMN_VISUAL_RIGHT: HUD_TOP_CONSTANTS.TARGET_PANEL_LEFT + HUD_TOP_CONSTANTS.PANEL_MIN_WIDTH,
} as const;

/** Measured viewport-space rect (CSS px) of the top-right minimap surface. */
export interface HudCompactMinimapRect {
  left: number;
  top: number;
  right: number;
  bottom: number;
}

/** Which anchor branch the compact quick menu took (evidence + tests). */
export type HudCompactQuickMenuAnchor = 'below-minimap' | 'left-of-minimap' | 'desktop';

/**
 * The rect the quick-menu anchor derives from: the live measurement when
 * usable, otherwise the documented shipped-minimap geometry (top/right 10,
 * 180×140 canvas + 1 px frame per side) pinned to the viewport's right edge.
 */
export function resolveCompactMinimapRect(
  rect: HudCompactMinimapRect | null | undefined,
  viewportWidth: number,
): HudCompactMinimapRect {
  if (
    rect
    && Number.isFinite(rect.left)
    && Number.isFinite(rect.top)
    && Number.isFinite(rect.right)
    && Number.isFinite(rect.bottom)
    && rect.right > rect.left
    && rect.bottom > rect.top
  ) {
    return { left: rect.left, top: rect.top, right: rect.right, bottom: rect.bottom };
  }
  const fallback = HUD_COMPACT_LAYOUT.quickMenu.minimapFallback;
  const frame = fallback.frame * 2;
  const right = viewportWidth - fallback.right;
  return {
    left: right - fallback.width - frame,
    top: fallback.top,
    right,
    bottom: fallback.top + fallback.height + frame,
  };
}

/** Border-box width of the scaled compact action-bar band. */
export const compactActionBarBandWidth = (): number =>
  Math.round(ACTION_BAR_NATURAL_WIDTH * HUD_COMPACT_LAYOUT.actionBar.scale);

/** Border-box height of the scaled compact action-bar band. */
export const compactActionBarBandHeight = (): number =>
  Math.round(ACTION_BAR_NATURAL_HEIGHT * HUD_COMPACT_LAYOUT.actionBar.scale);

export interface HudCompactLayoutOptions {
  viewportWidth?: number;
  viewportHeight?: number;
  /**
   * Top edge of the band below the banner (computed live by useHudTopLayout).
   * Falls back to the documented banner-clearance estimate when omitted.
   */
  bannerClearanceTop?: number;
  /** Whether the banner is mounted — drives the fallback clearance estimate. */
  hasBanner?: boolean;
  hasDebugText?: boolean;
  /**
   * Measured minimap rect (useHudMinimapRect). Omitted/null (jsdom, first
   * paint, minimap absent) → the documented shipped-minimap fallback geometry.
   */
  minimapRect?: HudCompactMinimapRect | null;
}

export interface HudCompactLayoutResult {
  isCompact: boolean;
  viewportWidth: number;
  viewportHeight: number;
  /** Visual scale applied to the action-bar band (bottom-centre origin). */
  actionBarScale: number;
  /** Bottom inset of the compact band (the shared HUD viewport margin). */
  actionBarBottomInset: number;
  /** Visual (scaled) band size / position. */
  actionBarBandWidth: number;
  actionBarBandHeight: number;
  actionBarBandTop: number;
  /** Chat peek width after the left-column clamp. */
  chatWidth: number;
  chatHeight: number;
  chatRightInset: number;
  chatBottomInset: number;
  chatTop: number;
  chatBottom: number;
  /** Which anchor branch the expanded quick menu took. */
  quickMenuAnchor: HudCompactQuickMenuAnchor;
  /** Quick-menu (expanded panel) anchor + cap. */
  quickMenuTop: number;
  quickMenuRightInset: number;
  /** Compact border-box width of the expanded panel (clamped by the column). */
  quickMenuWidth: number;
  quickMenuBottomInset: number;
  quickMenuMaxHeight: number;
}

/**
 * Compute the compact landscape band stack for a viewport. Pure — jsdom tests
 * assert the geometry contract from these values (N80 pattern: no layout
 * engine, no fake rects).
 */
export function computeHudCompactLayout(
  options: HudCompactLayoutOptions = {},
): HudCompactLayoutResult {
  const viewportWidth = options.viewportWidth
    ?? (typeof window !== 'undefined' ? window.innerWidth : 1440);
  const viewportHeight = options.viewportHeight
    ?? (typeof window !== 'undefined' ? window.innerHeight : 1000);
  const isCompact = viewportHeight <= HUD_COMPACT_LAYOUT.maxViewportHeight;
  // Live banner clearance (the same floor the target frame uses). Estimated
  // from the documented banner height when the banner is not measurable.
  const bannerClearanceTop = options.bannerClearanceTop
    ?? computeBannerClearanceTop({ hasBanner: options.hasBanner, hasDebugText: options.hasDebugText });

  const { GAP, VIEWPORT_MARGIN, LEFT_COLUMN_RIGHT } = HUD_COMPACT_CONSTANTS;
  const scale = HUD_COMPACT_LAYOUT.actionBar.scale;
  const chat = HUD_COMPACT_LAYOUT.chat;

  // Bottom band — the action bar scaled about its bottom-centre and docked at
  // the shared HUD viewport margin (the desktop dock is bottom: 20).
  const bandHeight = compactActionBarBandHeight();
  const bandWidth = compactActionBarBandWidth();
  const bandTop = viewportHeight - VIEWPORT_MARGIN - bandHeight;

  // Chat peek — right-anchored above the band, clamped right of the left column.
  const chatBottom = bandTop - GAP;
  const chatTop = chatBottom - chat.height;
  const availableChatWidth = viewportWidth - chat.rightInset - LEFT_COLUMN_RIGHT - GAP;
  const chatWidth = Math.max(
    chat.minWidth,
    Math.min(chat.maxWidth, availableChatWidth),
  );

  // Quick menu — right column. W2a-2 (2026-09-28): W2a anchored the panel at
  // the banner clearance, inside the minimap's vertical band (the shipped
  // defect: 624,76→820,182 behind minimap 652,10→834,152). The anchor now
  // derives from the measured minimap rect:
  //
  //   preferred  below-minimap: top = max(banner clearance, minimap bottom +
  //              GAP) when the slot down to the chat peek keeps the documented
  //              minimum usable height;
  //   fallback   left-of-minimap: keep the banner clearance and move the
  //              panel's right edge to minimap.left − GAP. At 844×390 the
  //              below-minimap slot is 20 px and at 640×360 it is 0, so the
  //              required short viewports necessarily take this branch — the
  //              measured minimap left edge is what keeps the panel clear.
  const quickMenuBottomY = chatTop - GAP;
  const minimap = resolveCompactMinimapRect(options.minimapRect, viewportWidth);
  const belowMinimapTop = Math.round(minimap.bottom + GAP);
  const belowMinimapHeight = quickMenuBottomY - belowMinimapTop;
  const belowMinimapUsable = belowMinimapHeight >= HUD_COMPACT_LAYOUT.quickMenu.minUsableHeight;

  let quickMenuAnchor: HudCompactQuickMenuAnchor;
  let quickMenuTop: number;
  let quickMenuRightInset: number;
  let quickMenuWidth: number;
  if (!isCompact) {
    // Desktop: the compact quick-menu fields keep their accepted values; the
    // measurable minimap must never move the > 450 layout.
    quickMenuAnchor = 'desktop';
    quickMenuTop = bannerClearanceTop;
    quickMenuRightInset = HUD_PANEL_DEFAULTS.quickMenu.position.offset;
    quickMenuWidth = HUD_PANEL_DEFAULTS.quickMenu.width;
  } else if (belowMinimapUsable) {
    quickMenuAnchor = 'below-minimap';
    quickMenuTop = Math.max(bannerClearanceTop, belowMinimapTop);
    quickMenuRightInset = HUD_PANEL_DEFAULTS.quickMenu.position.offset;
    quickMenuWidth = HUD_PANEL_DEFAULTS.quickMenu.width;
  } else {
    quickMenuAnchor = 'left-of-minimap';
    quickMenuTop = bannerClearanceTop;
    quickMenuRightInset = viewportWidth - Math.round(minimap.left - GAP);
    const availableWidth = Math.round(minimap.left - GAP)
      - HUD_COMPACT_CONSTANTS.LEFT_COLUMN_VISUAL_RIGHT
      - GAP;
    quickMenuWidth = Math.max(
      HUD_COMPACT_LAYOUT.quickMenu.minWidth,
      Math.min(HUD_PANEL_DEFAULTS.quickMenu.width, availableWidth),
    );
  }
  const quickMenuMaxHeight = Math.max(0, quickMenuBottomY - quickMenuTop);

  return {
    isCompact,
    viewportWidth,
    viewportHeight,
    actionBarScale: scale,
    actionBarBottomInset: VIEWPORT_MARGIN,
    actionBarBandWidth: bandWidth,
    actionBarBandHeight: bandHeight,
    actionBarBandTop: bandTop,
    chatWidth,
    chatHeight: chat.height,
    chatRightInset: chat.rightInset,
    chatBottomInset: viewportHeight - chatBottom,
    chatTop,
    chatBottom,
    quickMenuAnchor,
    quickMenuTop,
    quickMenuRightInset,
    quickMenuWidth,
    quickMenuBottomInset: viewportHeight - quickMenuBottomY,
    quickMenuMaxHeight,
  };
}

/**
 * Max height the target frame may take in the compact stack: from its own top
 * down to the action-band top, never across the GAP. Used by TargetInfoPanel
 * to cap the existing useHudTopLayout maxHeight (which stays the desktop
 * behaviour for heights > 450).
 */
export function compactTargetPanelMaxHeight(
  layout: HudCompactLayoutResult,
  targetPanelTop: number,
): number {
  return Math.max(0, layout.actionBarBandTop - HUD_COMPACT_CONSTANTS.GAP - targetPanelTop);
}

/** Live viewport size with a bounded resize listener (one per consumer). */
export function useHudViewportSize(): { width: number; height: number } {
  const [viewport, setViewport] = useState(() => ({
    width: typeof window !== 'undefined' ? window.innerWidth : 1440,
    height: typeof window !== 'undefined' ? window.innerHeight : 1000,
  }));

  useEffect(() => {
    const handleResize = () => {
      setViewport({ width: window.innerWidth, height: window.innerHeight });
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  return viewport;
}

/**
 * React hook for the compact landscape band stack. Recomputed on viewport
 * resize; `options` are read at render time (banner clearance from the top
 * layout hook's live measurement).
 */
export function useHudCompactLayout(
  options: HudCompactLayoutOptions = {},
): HudCompactLayoutResult {
  const viewport = useHudViewportSize();
  return computeHudCompactLayout({
    ...options,
    viewportWidth: viewport.width,
    viewportHeight: viewport.height,
  });
}

/** Structural equality for the measured minimap rect (null-safe). */
function sameMinimapRect(
  a: HudCompactMinimapRect | null,
  b: HudCompactMinimapRect | null,
): boolean {
  if (a === b) return true;
  if (!a || !b) return false;
  return a.left === b.left && a.top === b.top && a.right === b.right && a.bottom === b.bottom;
}

/**
 * W2a-2 (2026-09-28) — live rect of the top-right minimap surface
 * (`[data-hud-surface="minimap"]`), measured at LAYOUT time only:
 *
 * - once per effect run,
 * - on targeted ResizeObserver callbacks (minimap resize / hide / show),
 * - on window resize, font settlement, and a bounded container childList
 *   observer (mount/unmount of the surface or its collapsed "Map" button).
 *
 * The per-frame render path never reads the DOM — the anchor math consumes
 * this state in `computeHudCompactLayout`. Returns null while unmeasurable;
 * consumers fall back to the documented shipped-minimap geometry via
 * `resolveCompactMinimapRect`.
 */
export function useHudMinimapRect(): HudCompactMinimapRect | null {
  const [rect, setRect] = useState<HudCompactMinimapRect | null>(null);
  const useIsomorphicLayoutEffect = typeof window !== 'undefined' ? useLayoutEffect : useEffect;

  useIsomorphicLayoutEffect(() => {
    if (typeof document === 'undefined') return;

    let isDisposed = false;
    let observedElement: Element | null = null;
    let resizeObserver: ResizeObserver | null = null;
    let containerObserver: MutationObserver | null = null;

    const untrack = (): void => {
      resizeObserver?.disconnect();
      resizeObserver = null;
      observedElement = null;
    };

    const measure = (): void => {
      if (isDisposed) return;
      const element = document.querySelector('[data-hud-surface="minimap"]');
      if (!element) {
        untrack();
        setRect((previous) => (previous === null ? previous : null));
        return;
      }
      if (element !== observedElement) {
        untrack();
        observedElement = element;
        if (typeof ResizeObserver !== 'undefined') {
          resizeObserver = new ResizeObserver(() => measure());
          resizeObserver.observe(element);
        }
      }
      const measured = element.getBoundingClientRect();
      const next: HudCompactMinimapRect | null =
        measured.width > 0 && measured.height > 0 && measured.bottom > 0
          ? {
              left: measured.left,
              top: measured.top,
              right: measured.right,
              bottom: measured.bottom,
            }
          : null;
      setRect((previous) => (sameMinimapRect(previous, next) ? previous : next));
    };

    // Initial measurement + font settlement, mirroring useHudTopLayout.
    measure();
    if ('fonts' in document && (document.fonts as any)?.ready) {
      (document.fonts as any).ready
        .then(() => {
          if (!isDisposed) measure();
        })
        .catch(() => { /* ignore font loading errors */ });
    }

    const handleResize = (): void => measure();
    window.addEventListener('resize', handleResize);

    if (typeof MutationObserver !== 'undefined') {
      const container = document.getElementById('root') || document.body;
      // Any node added/removed under #root lands here (chat lines, cooldown
      // labels, tooltips — many per second in a fight). Re-measure only when
      // the minimap itself mounted, unmounted or was replaced: measure() reads
      // getBoundingClientRect, a forced layout (lane AL, 2026-10-08). Size
      // changes reach the ResizeObserver, which runs after layout.
      containerObserver = new MutationObserver(() => {
        if (document.querySelector('[data-hud-surface="minimap"]') !== observedElement) measure();
      });
      containerObserver.observe(container, { childList: true, subtree: true });
    }

    return () => {
      isDisposed = true;
      window.removeEventListener('resize', handleResize);
      untrack();
      containerObserver?.disconnect();
      containerObserver = null;
    };
  }, []);

  return rect;
}
